import {ImagePreviewDialog} from '@github-ui/agent-sessions-interface/components/ImagePreviewDialog'
import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  ALLOWED_DOCUMENT_TYPES,
  ALLOWED_IMAGE_EXTENSIONS,
  MAX_ATTACHMENT_SIZE_LABEL,
  MAX_INLINE_ATTACHMENTS,
} from '@github-ui/copilot-shared-ui/attachments/process-uploaded-files'
import {ImageAttachmentToken} from '@github-ui/copilot-shared-ui/components/ImageAttachmentToken'
import imageTokenStyles from '@github-ui/copilot-shared-ui/components/ImageAttachmentToken.module.css'
import type {ComposerAttachmentsController} from '@github-ui/copilot-shared-ui/hooks/use-composer-attachments'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash} from '@primer/react'
import type {RefObject} from 'react'

/** Image chips rendered above the textarea for attachments sent inline with an AHP prompt. */
export function EnvironmentImageAttachments({controller}: {controller: ComposerAttachmentsController}) {
  return (
    <ul className={imageTokenStyles.List} aria-label="Attached images">
      {controller.images.map(image => (
        <ImageAttachmentToken
          key={image.id}
          image={image}
          onRemove={controller.removeImage}
          onPreview={controller.setPreviewImage}
        />
      ))}
    </ul>
  )
}

/** The upload input, enlarged image preview, and skipped-file warning for AHP attachments. */
export function EnvironmentAttachmentControls({
  controller,
  fileInputRef,
}: {
  controller: ComposerAttachmentsController
  fileInputRef: RefObject<HTMLInputElement | null>
}) {
  return (
    <>
      {controller.previewImage && (
        <ImagePreviewDialog
          src={controller.previewImage.dataUrl}
          title={controller.previewImage.name}
          alt={controller.previewImage.name}
          onClose={() => controller.setPreviewImage(null)}
        />
      )}
      {controller.skippedFiles.length > 0 && (
        <Flash variant="warning" role="alert">
          {controller.skippedFiles.length === 1
            ? `${controller.skippedFiles[0]} could not be attached. Text and PDF files must be ${MAX_ATTACHMENT_SIZE_LABEL} or smaller, and you can attach up to ${MAX_INLINE_ATTACHMENTS} text, PDF, or image files at a time.`
            : `${controller.skippedFiles.length} files could not be attached: ${controller.skippedFiles.join(', ')}. Text and PDF files must be ${MAX_ATTACHMENT_SIZE_LABEL} or smaller, and you can attach up to ${MAX_INLINE_ATTACHMENTS} text, PDF, or image files at a time.`}
        </Flash>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        accept={`${ALLOWED_IMAGE_EXTENSIONS.join(',')},${ALLOWED_DOCUMENT_EXTENSIONS.join(',')},${ALLOWED_DOCUMENT_TYPES.join(',')},text/*`}
        onChange={controller.handleFileInputChange}
        data-testid="file-upload-input"
      />
    </>
  )
}
